import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Button } from "@pentabd/ui";

import { InputTextRow } from "@components";
import { CurrentPosition } from "./CurrentPosition";
import { RecommendedPosition } from "./RecommendedPosition";
import { SupportingDocument } from "./SupportingDocument";

import { STATUS } from "@constants/status.hrm";

import { PromotionDataType, PromotionDetailsType } from "@type/HRM/promotions";

import {
  InitiatePromotionFormValidationDataType,
  PROMOTION_SCORE,
  SUPPORTING_DOCUMENT,
  initiatePromotionFormValidation,
} from "@validations/HRM/promotion/initiatePromotionFormValidation";

import {
  deleteEmployeePromotion,
  initiateEmployeePromotion,
} from "@api/initiatePromotion";
import {
  convertDocumentData,
  restructureDocumentsData,
} from "@utils/documentDataConvert";
import { useState } from "react";

const { SUCCESS } = STATUS;

export const AccordionBody = ({
  promotionData,
  promotionDetailsData,
  isDisabled,
  handleDelete,
  handleUpdate,
  setIsSuccessModalOpen,
  setIsErrorModalOpen,
}: {
  promotionData: PromotionDataType;
  promotionDetailsData: PromotionDetailsType;
  isDisabled: boolean;
  handleDelete: (arg: number) => void;
  handleUpdate: (promotionDetailsData: PromotionDetailsType) => void;
  setIsSuccessModalOpen: (arg: boolean) => void;
  setIsErrorModalOpen: (arg: boolean) => void;
}) => {
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { id: promotionId } = promotionData;
  const {
    employee: {
      id: employeeId,
      gradeId: employeeGradeId,
      positionId: employeePositionId,
      officeName,
    },
    promotionDetailsId,
    uploadDocument,
    posting,
    examinationScore,
    unitId,
    gradeId,
    positionId,
    unitPositionId,
    fromDate,
    toDate,
    goDate,
    goNo,
    basicSalary,
    natureOfPromotion,
  } = promotionDetailsData as any;

  const methods = useForm<InitiatePromotionFormValidationDataType>({
    resolver: yupResolver(initiatePromotionFormValidation),
    values: {
      gradeId: employeeGradeId,
      positionId: employeePositionId,
      officeName,
      uploadDocument,
      ...restructureDocumentsData(uploadDocument),
      posting,
      examinationScore,
      unitId,
      unitPositionId,
      recommendedPositionId: positionId,
      recommendedGradeId: gradeId,
      basicSalary,
      toDate,
      goDate,
      goNo,
      fromDate,
      natureOfPromotion,
    },
  });

  const {
    handleSubmit,
    register,
    formState: { errors },
  } = methods;

  const onSubmit: SubmitHandler<InitiatePromotionFormValidationDataType> = (
    data: InitiatePromotionFormValidationDataType,
  ) => {
    setIsLoading(true);
    const convertedData = convertDocumentData(data, SUPPORTING_DOCUMENT);
    initiateEmployeePromotion(
      promotionId,
      promotionDetailsId,
      {
        unitPositionId: convertedData.unitPositionId,
        posting: convertedData.posting,
        employeeMasterId: employeeId,
        gradeId: convertedData.recommendedGradeId,
        examinationScore: convertedData.examinationScore,
        uploadDocument: convertedData.uploadDocument,
        fromDate: convertedData.fromDate || "",
        toDate: convertedData.toDate || "",
        goDate: convertedData.goDate || "",
        goNo: convertedData.goNo || "",
        basicSalary: convertedData.basicSalary || 0,
        natureOfPromotion: convertedData.natureOfPromotion || "",
      },
      (e) => {
        if (e.status === SUCCESS) {
          setIsSuccessModalOpen(true);
          handleUpdate(e.data);
        } else setIsErrorModalOpen(true);
        setIsLoading(false);
      },
    );
  };

  const handlePromotionDeleteDetails = () => {
    setIsLoading(true);
    deleteEmployeePromotion(promotionId, promotionDetailsId, (e) => {
      if (e.status === SUCCESS) {
        setIsSuccessModalOpen(true);
        handleDelete(promotionDetailsId);
      } else {
        setIsErrorModalOpen(true);
      }
      setIsLoading(false);
    });
  };

  const updateApplication =
    positionId && unitPositionId && gradeId && unitId !== null;

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} className="mt-8 pt-8 border-top">
        <CurrentPosition />
        <RecommendedPosition isDisabled={isDisabled} />
        <div className="mb-8 border-bottom pb-8">
          <InputTextRow
            label="INITIATE_PROMOTION.FORM.POSITION.RECOMMENDED.SCORE"
            register={{ ...register(PROMOTION_SCORE) }}
            error={errors as any}
            disabled={isDisabled}
            required
          />
        </div>
        <SupportingDocument isDisabled={isDisabled} />
        {!isDisabled && (
          <div className="d-flex justify-content-end gap-6 px-10 py-8 border-top">
            <Button
              size="sm"
              fill="fill"
              type="danger"
              onClick={handlePromotionDeleteDetails}
              loading={isLoading}
            >
              {t("INITIATE_PROMOTION.FORM.BUTTON.DELETE")}
            </Button>
            <Button
              size="sm"
              fill="fill"
              type="primary"
              htmlType="submit"
              loading={isLoading}
            >
              {t(
                `INITIATE_PROMOTION.FORM.BUTTON.${
                  updateApplication ? "UPDATE" : "SAVE"
                }`,
              )}
            </Button>
          </div>
        )}
      </form>
    </FormProvider>
  );
};
